import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import moodTrackerService from '../../services/moodTrackerService';
import { TIME_RANGES } from '../../utils/moodTrackerUtils';
import { Button, Card, CloseButton, Spinner, Alert, Select } from '../design-system';
import MoodChart from './MoodChart';
import MoodComments from './MoodComments';
import { toast } from 'react-hot-toast';

const MoodHistoryModal = ({ 
  isOpen, 
  onClose, 
  patientId = null, 
  title = "Mood History" 
}) => {
  const { user } = useAuth();
  const [moodHistory, setMoodHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState('week');
  const [selectedMoodFilter, setSelectedMoodFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('chart'); // 'chart' or 'comments'

  // Load mood history when modal opens or time range changes
  useEffect(() => {
    if (isOpen) {
      loadMoodHistory();
    }
  }, [isOpen, selectedTimeRange, patientId]);

  const loadMoodHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let data;
      
      if (patientId) {
        // Therapist view - get patient's mood history
        data = await moodTrackerService.getPatientMoodHistory(
          patientId, 
          selectedTimeRange, 
          user
        );
      } else {
        // Patient view - get own mood history
        data = await moodTrackerService.getMoodHistory(
          selectedTimeRange, 
          user
        );
      }
      
      setMoodHistory(data);
    } catch (err) {
      console.error('Error loading mood history:', err);
      setError('Failed to load mood history. Please try again.');
      toast.error('Failed to load mood history');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeRangeChange = (e) => {
    const newRange = e.target.value;
    console.log(`Time range changed from ${selectedTimeRange} to ${newRange}`);
    // Clear cache for mood history to ensure fresh data
    moodTrackerService.clearCacheByPattern('mood-history');
    if (patientId) {
      moodTrackerService.clearCacheByPattern(`patient-mood-history-${patientId}`);
    }
    setSelectedTimeRange(newRange);
  };

  const handleMoodFilterChange = (newFilter) => {
    setSelectedMoodFilter(newFilter);
  };

  const handleRefresh = () => {
    console.log('Refreshing mood history - clearing cache');
    // Clear all mood-related cache to ensure fresh data
    moodTrackerService.clearCacheByPattern('mood-history');
    if (patientId) {
      moodTrackerService.clearCacheByPattern(`patient-mood-history-${patientId}`);
    }
    loadMoodHistory();
  };

  if (!isOpen) return null;

  // Safety check for SSR or when document.body is not available
  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {title}
          </h2>
          <CloseButton onClick={onClose} />
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto overflow-x-visible max-h-[calc(90vh-120px)]" style={{ overflowX: 'visible' }}>
          {error && (
            <Alert variant="error" className="mb-6">
              {error}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                className="ml-3"
              >
                Retry
              </Button>
            </Alert>
          )}

          {/* Time Range Selector and Tab Navigation - Combined */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
            <Select
              value={selectedTimeRange}
              onChange={handleTimeRangeChange}
              className="w-40"
              options={TIME_RANGES.map(range => ({
                value: range.id,
                label: range.label
              }))}
            />
            <Button
              variant={activeTab === 'chart' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('chart')}
              size="small"
              className="px-3"
            >
              📊
            </Button>
            <Button
              variant={activeTab === 'comments' ? 'primary' : 'outline'}
              onClick={() => setActiveTab('comments')}
              size="small"
              className="px-3"
            >
              💭
            </Button>
          </div>

          {/* Chart Tab */}
          {activeTab === 'chart' && (
            <MoodChart
              moodHistory={moodHistory}
              loading={loading}
              selectedTimeRange={selectedTimeRange}
            />
          )}

          {/* Comments Tab */}
          {activeTab === 'comments' && (
            <MoodComments
              moodHistory={moodHistory}
              loading={loading}
              onMoodFilterChange={handleMoodFilterChange}
              selectedMoodFilter={selectedMoodFilter}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Refreshing...
              </>
            ) : (
              'Refresh'
            )}
          </Button>
          <Button onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal outside of overflow containers
  return createPortal(modalContent, document.body);
};

export default MoodHistoryModal;
